/**
 * Sprint API Service (Enhanced based on monday.com)
 * Comprehensive sprint management operations
 */

import axiosClient from '../../../api/axiosClient';
import {
  Sprint,
  SprintOverview,
  BurndownChart,
  SprintInsights,
  CreateSprintDTO,
  UpdateSprintDTO,
  CompleteSprintDTO,
  MoveTasksDTO,
  KanbanBoard,
  SprintAutomation,
} from '../model/types';

// Backend's SprintStatus enum (src/entities/sprint.entity.ts) is lowercase
// ('planned' | 'active' | 'completed'); the frontend Sprint type uses
// uppercase SCREAMING_CASE values throughout (status badges, health
// indicators, Start/Complete button visibility, etc). Normalize at the API
// boundary so every consumer can keep comparing against the uppercase values.
const BACKEND_STATUS_MAP: Record<string, Sprint['status']> = {
  planned: 'NOT_STARTED',
  active: 'ACTIVE',
  completed: 'COMPLETED',
};

const normalizeSprint = (raw: any): Sprint => ({
  ...raw,
  id: String(raw.id),
  status: BACKEND_STATUS_MAP[raw.status] || raw.status,
  isActive: raw.isActive ?? raw.status === 'active',
});

export const sprintService = {
  // ==================== Sprint CRUD ====================

  /**
   * Get all sprints for a board
   */
  getSprintsForBoard: async (boardId: string): Promise<Sprint[]> => {
    // Backend route: GET /api/sprints/board/:boardId
    const response = await axiosClient.get(`/sprints/board/${boardId}`);
    const sprints = Array.isArray(response.data) ? response.data : response.data.sprints || [];
    return sprints.map(normalizeSprint);
  },

  /**
   * Get a specific sprint by ID
   */
  getSprint: async (sprintId: string): Promise<Sprint> => {
    const response = await axiosClient.get(`/sprints/${sprintId}`);
    return normalizeSprint(response.data);
  },

  /**
   * Create a new sprint
   */
  createSprint: async (data: CreateSprintDTO): Promise<Sprint> => {
    const payload = {
      name: data.name,
      startDate: data.startDate,
      endDate: data.endDate,
      goal: data.goal || (data.goals && data.goals.length > 0 ? data.goals[0] : undefined),
      boardId: Number(data.boardId) || 1,
    };
    const response = await axiosClient.post('/sprints', payload);
    return normalizeSprint(response.data);
  },

  /**
   * Update an existing sprint
   */
  updateSprint: async (sprintId: string, updates: UpdateSprintDTO): Promise<Sprint> => {
    // Backend uses PATCH not PUT
    const response = await axiosClient.patch(`/sprints/${sprintId}`, updates);
    return normalizeSprint(response.data);
  },

  /**
   * Delete a sprint (only if NOT_STARTED)
   */
  deleteSprint: async (sprintId: string, moveTasksTo?: string): Promise<void> => {
    const params = moveTasksTo ? `?moveTasksTo=${moveTasksTo}` : '';
    await axiosClient.delete(`/sprints/${sprintId}${params}`);
  },

  // ==================== Sprint Lifecycle ====================

  /**
   * Start a sprint
   */
  startSprint: async (sprintId: string): Promise<Sprint> => {
    const response = await axiosClient.post(`/sprints/${sprintId}/start`);
    return normalizeSprint(response.data);
  },

  /**
   * Reset a sprint (change start date to today)
   */
  resetSprint: async (sprintId: string): Promise<Sprint> => {
    const response = await axiosClient.post(`/sprints/${sprintId}/reset`);
    return normalizeSprint(response.data);
  },

  /**
   * Complete a sprint
   */
  completeSprint: async (sprintId: string, data: CompleteSprintDTO): Promise<Sprint> => {
    const response = await axiosClient.post(`/sprints/${sprintId}/complete`, data);
    return normalizeSprint(response.data);
  },

  /**
   * Get the active sprint for a board
   */
  getActiveSprint: async (boardId: string): Promise<Sprint | null> => {
    try {
      // Backend route: GET /api/sprints/board/:boardId — filter active client-side
      const response = await axiosClient.get(`/sprints/board/${boardId}`);
      const sprints: Sprint[] = (Array.isArray(response.data) ? response.data : response.data.sprints || []).map(
        normalizeSprint
      );
      return sprints.find((s) => s.status === 'ACTIVE') || null;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      console.warn('🔄 Backend not available, using mock active sprint data');
      return null;
    }
  },

  // ==================== Sprint Overview ====================

  /**
   * Get comprehensive sprint overview
   */
  getSprintOverview: async (sprintId: string): Promise<SprintOverview> => {
    const response = await axiosClient.get(`/sprints/${sprintId}/overview`);
    return response.data;
  },

  /**
   * Add update to sprint
   */
  addSprintUpdate: async (sprintId: string, content: string): Promise<void> => {
    await axiosClient.post(`/sprints/${sprintId}/updates`, { content });
  },

  /**
   * Upload file to sprint
   */
  uploadSprintFile: async (sprintId: string, file: File): Promise<void> => {
    const formData = new FormData();
    formData.append('file', file);
    await axiosClient.post(`/sprints/${sprintId}/files`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  // ==================== Task Management ====================

  /**
   * Move tasks to sprint
   */
  moveTasksToSprint: async (sprintId: string, data: MoveTasksDTO): Promise<void> => {
    await axiosClient.post(`/sprints/${sprintId}/tasks`, data);
  },

  /**
   * Remove tasks from sprint
   */
  removeTasksFromSprint: async (sprintId: string, taskIds: string[]): Promise<void> => {
    await axiosClient.delete(`/sprints/${sprintId}/tasks`, {
      data: { taskIds },
    });
  },

  /**
   * Get tasks for a sprint
   */
  getSprintTasks: async (sprintId: string): Promise<any[]> => {
    const response = await axiosClient.get(`/sprints/${sprintId}/tasks`);
    return response.data;
  },

  // ==================== Burndown Chart ====================

  /**
   * Get burndown chart data
   */
  getBurndownChart: async (
    sprintId: string,
    unit: 'STORY_POINTS' | 'TASKS' | 'HOURS' = 'STORY_POINTS'
  ): Promise<BurndownChart> => {
    const response = await axiosClient.get(`/sprints/${sprintId}/burndown?unit=${unit}`);
    return response.data;
  },

  /**
   * Refresh burndown chart data
   */
  refreshBurndownChart: async (sprintId: string): Promise<BurndownChart> => {
    const response = await axiosClient.post(`/sprints/${sprintId}/burndown/refresh`);
    return response.data;
  },

  // ==================== Agile Insights ====================

  /**
   * Get sprint insights (Velocity, Planned vs Unplanned)
   */
  getSprintInsights: async (sprintId: string, limit: number = 10): Promise<SprintInsights> => {
    const response = await axiosClient.get(`/sprints/${sprintId}/insights`);
    return response.data;
  },

  /**
   * Get velocity chart data
   */
  getVelocityChart: async (boardId: string, sprintCount: number = 6): Promise<any> => {
    const response = await axiosClient.get(`/boards/${boardId}/velocity?count=${sprintCount}`);
    return response.data;
  },

  /**
   * Get planned vs unplanned chart data
   */
  getPlannedVsUnplannedChart: async (id: string, sprintCount: number = 6): Promise<any> => {
    const response = await axiosClient.get(`/sprints/${id}/planned-vs-unplanned`);
    return response.data;
  },

  // ==================== Kanban View ====================

  /**
   * Get Kanban board for sprint
   */
  getSprintKanban: async (sprintId: string): Promise<KanbanBoard> => {
    const response = await axiosClient.get(`/sprints/${sprintId}/kanban`);
    return response.data;
  },

  /**
   * Move a task between Kanban columns and/or reorder it within a column.
   *
   * Uses optimistic concurrency: `expectedVersion` must be the version the
   * caller last read. A 409 response means another move landed first; its
   * body includes the task's current server-side state so the caller can
   * reconcile instead of blindly retrying.
   */
  moveTask: async (
    taskId: string,
    dto: { expectedVersion: number; status?: string; position?: number },
  ): Promise<{ id: string; status: string; position: number; version: number }> => {
    const response = await axiosClient.patch(`/tasks/${taskId}/move`, dto);
    return response.data;
  },

  // ==================== Sprint Automations ====================

  /**
   * Get available sprint automations
   */
  getSprintAutomations: async (boardId: string): Promise<SprintAutomation[]> => {
    const response = await axiosClient.get(`/boards/${boardId}/sprint-automations`);
    return response.data;
  },

  /**
   * Create sprint automation
   */
  createSprintAutomation: async (boardId: string, automation: Partial<SprintAutomation>): Promise<SprintAutomation> => {
    const response = await axiosClient.post(`/boards/${boardId}/sprint-automations`, automation);
    return response.data;
  },

  /**
   * Toggle automation enabled/disabled
   */
  toggleSprintAutomation: async (automationId: string, enabled: boolean): Promise<void> => {
    await axiosClient.patch(`/sprint-automations/${automationId}`, { enabled });
  },

  /**
   * Delete sprint automation
   */
  deleteSprintAutomation: async (automationId: string): Promise<void> => {
    await axiosClient.delete(`/sprint-automations/${automationId}`);
  },

  // ==================== Backlog Management ====================

  /**
   * Get backlog tasks (not assigned to any sprint)
   */
  getBacklogTasks: async (boardId: string): Promise<any[]> => {
    const response = await axiosClient.get(`/boards/${boardId}/backlog`);
    return response.data;
  },

  /**
   * Prioritize backlog (reorder tasks)
   */
  reorderBacklog: async (boardId: string, taskIds: string[]): Promise<void> => {
    await axiosClient.put(`/boards/${boardId}/backlog/reorder`, { taskIds });
  },

  // ==================== Sprint Goals ====================

  /**
   * Add goal to sprint
   */
  addSprintGoal: async (sprintId: string, description: string): Promise<void> => {
    await axiosClient.post(`/sprints/${sprintId}/goals`, { description });
  },

  /**
   * Update goal
   */
  updateSprintGoal: async (goalId: string, updates: { description?: string; completed?: boolean }): Promise<void> => {
    await axiosClient.patch(`/sprint-goals/${goalId}`, updates);
  },

  /**
   * Delete goal
   */
  deleteSprintGoal: async (goalId: string): Promise<void> => {
    await axiosClient.delete(`/sprint-goals/${goalId}`);
  },

  // ==================== Reports & Export ====================

  /**
   * Export sprint report
   */
  exportSprintReport: async (sprintId: string, format: 'pdf' | 'excel' | 'csv'): Promise<Blob> => {
    const response = await axiosClient.get(`/sprints/${sprintId}/export?format=${format}`, {
      responseType: 'blob',
    });
    return response.data;
  },
};

export default sprintService;
